import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, input, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { isNullish } from '@examdojo/core/util/nullish';
import { QuestionItemTypeEnum } from '@examdojo/models/question';
import { ContentItemFormGroup } from '@examdojo/question-content-item';
import { SelectorBadgeComponent } from '@examdojo/ui/selector-badge';
import { distinctUntilChanged, of, startWith, switchMap, tap } from 'rxjs';
import {
  ActiveQuestionEditorSubItemOptionsCoordinates,
  CONTENT_ITEM_TYPE_TO_COLOR,
  CONTENT_ITEM_TYPE_TO_LONG_LABEL,
  CONTENT_ITEM_TYPE_TO_SHORT_LABEL,
  OptionsItemFormGroup,
} from '../question-item-editor.model';
import { QuestionItemsFormService } from '../question-items-form.service';
import { QuestionItemOptionsContentViewerComponent } from './question-item-options-content-viewer.component';

@Component({
  selector: 'dojo-question-item-options-viewer',
  styleUrls: ['./question-item-options-viewer.component.scss'],
  imports: [QuestionItemOptionsContentViewerComponent, SelectorBadgeComponent, MatTooltip, NgStyle],
  template: `
    @if (state.editingOptions) {
      @if (optionsFormGroup(); as optionsFormGroup) {
        <dojo-question-item-options-content-viewer
          [optionsFormGroup]="optionsFormGroup"
          [isAllowedToEditImage]="state.editingOptions.allowImageEditing.length > 0"
          [isAllowedToEditTable]="
            state.editingOptions.allowImageEditing.length > 0 || state.editingOptions.allowQuestionEditing
          "
          [correctOptionIndexes]="state.correctOptionIndexesValue"
          [showWorkedSolution]="!!state.workedSolutionValue"
          (editItem)="editOptionSubItem($event)"
          (editOptionItem)="editOptionSubItem($event)"
          (editWorkedSolutionItem)="editWorkedSolutionItem($event)"
        />
        @if (!state.editingOptions.hideBadges || state.editingOptions.allowQuestionEditing) {
          <div
            class="wrap-badge"
            [ngStyle]="{
              '--after-color': 'var(--' + CONTENT_ITEM_TYPE_TO_COLOR[QuestionItemTypeEnum.Options] + '-200)'
            }"
          >
            <dojo-selector-badge
              class="self-start"
              (clicked)="setActiveQuestionItem.emit()"
              [color]="CONTENT_ITEM_TYPE_TO_COLOR[QuestionItemTypeEnum.Options]"
              [label]="CONTENT_ITEM_TYPE_TO_SHORT_LABEL[QuestionItemTypeEnum.Options]"
              [active]="activeQuestionItem()?.ctrl === optionsFormGroup"
              [disabled]="isSelectingDisabled()"
              [matTooltip]="CONTENT_ITEM_TYPE_TO_LONG_LABEL[QuestionItemTypeEnum.Options]"
            />
          </div>
        }
      }
    }
  `,
  host: {
    class: 'flex justify-between gap-2',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionItemOptionsViewerComponent {
  constructor(
    private readonly questionItemsFormService: QuestionItemsFormService,
    private readonly cdRef: ChangeDetectorRef,
  ) {
    toObservable(this.optionsFormGroup)
      .pipe(
        switchMap((formCtrl) => formCtrl.controls.options.valueChanges),
        distinctUntilChanged((a, b) => a.length === b.length),
        tap(() => this.cdRef.markForCheck()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly optionsFormGroup = input.required<OptionsItemFormGroup>();
  readonly isSelectingDisabled = input.required<boolean>();

  readonly editItem = output<{
    item: ContentItemFormGroup;
    coordinates: ActiveQuestionEditorSubItemOptionsCoordinates;
  }>();

  readonly setActiveQuestionItem = output<void>();

  readonly activeQuestionItem = this.questionItemsFormService.activeQuestionItem;
  readonly QuestionItemTypeEnum = QuestionItemTypeEnum;
  readonly CONTENT_ITEM_TYPE_TO_COLOR = CONTENT_ITEM_TYPE_TO_COLOR;
  readonly CONTENT_ITEM_TYPE_TO_SHORT_LABEL = CONTENT_ITEM_TYPE_TO_SHORT_LABEL;
  readonly CONTENT_ITEM_TYPE_TO_LONG_LABEL = CONTENT_ITEM_TYPE_TO_LONG_LABEL;

  readonly correctOptionIndexesValue$ = toObservable(this.optionsFormGroup).pipe(
    switchMap((formCtrl) =>
      formCtrl.controls.markscheme.controls.correct_option_indexes.valueChanges.pipe(
        startWith(formCtrl.controls.markscheme.controls.correct_option_indexes.value),
      ),
    ),
  );

  readonly workedSolutionValue$ = toObservable(this.optionsFormGroup).pipe(
    switchMap((formCtrl) =>
      formCtrl.controls.markscheme.controls.worked_solution
        ? formCtrl.controls.markscheme.controls.worked_solution.valueChanges.pipe(
            startWith(formCtrl.controls.markscheme.controls.worked_solution.value),
          )
        : of(null),
    ),
  );

  readonly state = connectState({
    correctOptionIndexesValue: this.correctOptionIndexesValue$,
    editingOptions: this.questionItemsFormService.editingOptions$,
    workedSolutionValue: this.workedSolutionValue$,
  });

  editOptionSubItem(
    event:
      | { item: ContentItemFormGroup; contentItemIndex: number }
      | { item: ContentItemFormGroup; contentItemIndex: number; optionIndex: number },
  ) {
    const { item, contentItemIndex } = event;
    const optionIndex = 'optionIndex' in event ? event.optionIndex : undefined;

    this.editItem.emit({
      item,
      coordinates: isNullish(optionIndex)
        ? { content_items: contentItemIndex }
        : { content_items: contentItemIndex, options: optionIndex },
    });
  }

  editWorkedSolutionItem({ item, contentItemIndex }: { item: ContentItemFormGroup; contentItemIndex: number }) {
    this.editItem.emit({
      item,
      coordinates: { content_items: contentItemIndex, worked_solution: true },
    });
  }
}
